/**
 * Reparto del cobro en efectivo, en espejo de `Dinero` del backend.
 *
 * El servidor sigue siendo la autoridad: esto solo sirve para que el POS
 * muestre el importe correcto **antes** de enviar el cobro. Si las dos cuentas
 * no coinciden, el backend rechaza la operación diciendo cuál corresponde.
 *
 * Se llamaba `dinero.ts` y daba por sentado el peso chileno: enteros y múltiplo
 * de diez. Ahora las reglas llegan de la configuración, porque en Perú el
 * múltiplo es S/ 0,10 y el redondeo nunca sube.
 */

export interface ReglasEfectivo {
    decimales: number
    /** Múltiplo al que se lleva el efectivo, en unidades de la moneda. */
    multiplo: number
    /** Si el redondeo va siempre al múltiplo de abajo, a favor del consumidor. */
    haciaAbajo: boolean
}

export const REGLAS_POR_DEFECTO: ReglasEfectivo = { decimales: 0, multiplo: 10, haciaAbajo: false }

/*
 * Todo se calcula en la unidad mínima de la moneda —céntimos, o pesos enteros—
 * y con enteros. No es una manía: en coma flotante `0.30 / 0.10` da
 * 2,9999999999999996, cuya parte entera es 2, así que S/ 0,30 se "redondearía"
 * a S/ 0,20. Barriendo de 0 a 2.000 soles de céntimo en céntimo, dividir por la
 * fracción falla en 6.457 de los 200.001 importes; convertir a céntimos y
 * dividir por 10 no falla en ninguno. El backend usa `decimal` y no tiene este
 * problema; el navegador solo tiene `number`.
 */
function factor(decimales: number): number {
    return 10 ** decimales
}

function aMinimos(valor: number, decimales: number): number {
    return Math.sign(valor) * Math.round(Math.abs(valor) * factor(decimales))
}

/** Redondea a los decimales de la moneda, con los empates alejándose del cero. */
export function importe(valor: number, reglas: ReglasEfectivo): number {
    return aMinimos(valor, reglas.decimales) / factor(reglas.decimales)
}

/** Lleva el efectivo al múltiplo que circula. Con múltiplo 0 solo ajusta los decimales. */
export function redondearEfectivo(valor: number, reglas: ReglasEfectivo): number {
    const f = factor(reglas.decimales)
    const exacto = aMinimos(valor, reglas.decimales)
    const multiplo = Math.round(reglas.multiplo * f)

    if (multiplo <= 1) return exacto / f

    const enMultiplos = Math.abs(exacto) / multiplo
    const redondeado = reglas.haciaAbajo ? Math.floor(enMultiplos) : Math.round(enMultiplos)

    return (Math.sign(exacto) * redondeado * multiplo) / f
}

/**
 * Efectivo que debe entrar a la caja y el ajuste que introduce el redondeo.
 * El redondeo se aplica **solo a la parte en efectivo**: quien paga todo con
 * débito no debe pagar de más.
 */
export function calcularEfectivo(
    total: number,
    pagadoSinEfectivo: number,
    credito: number,
    reglas: ReglasEfectivo,
): { requerido: number; redondeo: number; restante: number } {
    const restante = importe(total - pagadoSinEfectivo - credito, reglas)
    const requerido = restante <= 0 ? 0 : redondearEfectivo(restante, reglas)

    return { requerido, redondeo: importe(requerido - restante, reglas), restante }
}
